import { AxiosResponse } from 'axios';
import {
  IAdminOrganizationListResponse,
  IAdminOrganizationQueryParams,
  IAdminOrganizationActionResponse,
  IRejectOrganizationPayload,
  IRevokeOrganizationPayload,
  IAdminOrganizationResponse,
  IUpdateAdminOrganizationPayload,
  ICreateAdminOrganizationPayload,
  IUpdateOrganizationStatusPayload,
  IOrganizationStatusResponse,
  IOrganizationDocumentsQueryParams,
  IOrganizationDocumentsResponse,
  IOrganizationInvoicesQueryParams,
  IOrganizationInvoicesResponse,
  IMarkOrganizationInvoicePaidPayload,
  IMarkOrganizationInvoicePaidResponse,
  IDeleteOrganizationPayload,
  IDeleteOrganizationResponse,
  IResetInstitutePayload,
  IResetInstituteResponse,
  IInstituteResetStatusResponse,
  IActiveInstituteResetResponse,
  IOrganizationStatisticsResponse,
} from '../../../interface/classveew-admin/organization.interface';
import { adminInstance } from '../../api';

export const adminOrganizationService = {
  /**
   * Fetch all organizations with optional query parameters
   */
  fetchOrganizations: async (
    params?: IAdminOrganizationQueryParams,
  ): Promise<AxiosResponse<IAdminOrganizationListResponse>> => {
    const response = await adminInstance.get('/organizations', {
      params,
    });
    return response;
  },

  /**
   * Create a new organization
   */
  createOrganization: async (
    payload: ICreateAdminOrganizationPayload,
  ): Promise<AxiosResponse<IAdminOrganizationResponse>> => {
    const response = await adminInstance.post('/organizations', payload);
    return response;
  },

  /**
   * Approve an organization
   */
  approveOrganization: async (
    organizationId: string,
  ): Promise<AxiosResponse<IAdminOrganizationActionResponse>> => {
    const response = await adminInstance.patch(`/organizations/${organizationId}/approve`);
    return response;
  },

  /**
   * Reject an organization
   */
  rejectOrganization: async (
    organizationId: string,
    payload: IRejectOrganizationPayload,
  ): Promise<AxiosResponse<IAdminOrganizationActionResponse>> => {
    const response = await adminInstance.patch(`/organizations/${organizationId}/reject`, payload);
    return response;
  },

  /**
   * Revoke an organization
   */
  revokeOrganization: async (
    organizationId: string,
    payload: IRevokeOrganizationPayload,
  ): Promise<AxiosResponse<IAdminOrganizationActionResponse>> => {
    const response = await adminInstance.patch(`/organizations/${organizationId}/revoke`, payload);
    return response;
  },

  /**
   * Delete an institute request (soft archive or hard permanent delete)
   */
  deleteOrganization: async (
    organizationId: string,
    payload: IDeleteOrganizationPayload,
  ): Promise<AxiosResponse<IDeleteOrganizationResponse>> => {
    const response = await adminInstance.delete(`/organizations/${organizationId}`, {
      data: payload,
    });
    return response;
  },

  /**
   * Get organization by ID
   */
  getOrganizationById: async (
    organizationId: string,
  ): Promise<AxiosResponse<IAdminOrganizationResponse>> => {
    const response = await adminInstance.get(`/organizations/${organizationId}`);
    return response;
  },

  /**
   * Update an organization
   */
  updateOrganization: async (
    organizationId: string,
    payload: IUpdateAdminOrganizationPayload,
  ): Promise<AxiosResponse<IAdminOrganizationResponse>> => {
    const response = await adminInstance.put(`/organizations/${organizationId}`, payload);
    return response;
  },

  /**
   * Update organization status (active/inactive)
   */
  updateOrganizationStatus: async (
    organizationId: string,
    payload: IUpdateOrganizationStatusPayload,
  ): Promise<AxiosResponse<IOrganizationStatusResponse>> => {
    const response = await adminInstance.patch(`/organizations/${organizationId}/status`, payload);
    return response;
  },

  /**
   * Get all school types
   */
  getAllSchoolTypes: async (): Promise<
    AxiosResponse<{
      status: number;
      message: string;
      data: { schoolTypes: { id: string; title: string }[] };
    }>
  > => {
    const response = await adminInstance.get('/organizations/school-types');
    return response;
  },

  /**
   * Get organization documents
   */
  getOrganizationDocuments: async (
    organizationId: string,
    params?: IOrganizationDocumentsQueryParams,
  ): Promise<AxiosResponse<IOrganizationDocumentsResponse>> => {
    const response = await adminInstance.get(
      `/organizations/${organizationId}/documents`,
      { params },
    );
    return response;
  },

  /**
   * Get organization invoices
   */
  getOrganizationInvoices: async (
    organizationId: string,
    params?: IOrganizationInvoicesQueryParams,
  ): Promise<AxiosResponse<IOrganizationInvoicesResponse>> => {
    const response = await adminInstance.get(
      `/organizations/${organizationId}/invoices`,
      { params },
    );
    return response;
  },

  /**
   * Mark an organization invoice as paid (manual cash payment)
   */
  markInvoiceAsPaid: async (
    payload: IMarkOrganizationInvoicePaidPayload,
  ): Promise<AxiosResponse<IMarkOrganizationInvoicePaidResponse>> => {
    const { organizationId, invoiceId, ...body } = payload;
    const response = await adminInstance.post(
      `/organizations/${organizationId}/invoices/${invoiceId}/mark-paid`,
      body,
    );
    return response;
  },

  /**
   * Reset (soft-delete) a live institute's selected operational data.
   * Dispatches an async purge job; poll the returned queueId for completion.
   */
  resetInstitute: async (
    organizationId: string,
    payload: IResetInstitutePayload,
  ): Promise<AxiosResponse<IResetInstituteResponse>> => {
    const response = await adminInstance.post(
      `/organizations/${organizationId}/reset`,
      payload,
    );
    return response;
  },

  /**
   * Poll the status of an institute reset job.
   */
  getInstituteResetStatus: async (
    queueId: string,
  ): Promise<AxiosResponse<IInstituteResetStatusResponse>> => {
    const response = await adminInstance.get(
      `/organizations/reset/${queueId}/status`,
    );
    return response;
  },

  /**
   * Get the latest reset job for an organization (to resume the in-progress
   * indicator after the user navigates away and back).
   */
  getActiveInstituteReset: async (
    organizationId: string,
  ): Promise<AxiosResponse<IActiveInstituteResetResponse>> => {
    const response = await adminInstance.get(
      `/organizations/${organizationId}/reset/active`,
    );
    return response;
  },

  /**
   * Live statistics for an approved organization (composition, finance,
   * activity, reset history).
   */
  getOrganizationStatistics: async (
    organizationId: string,
  ): Promise<AxiosResponse<IOrganizationStatisticsResponse>> => {
    const response = await adminInstance.get(
      `/organizations/${organizationId}/statistics`,
    );
    return response;
  },
};
